<template>
  <div ref="dropdownWrapper" data-cy="placement-workflow-template-field">
    <lf-dropdown-with-api-search
      ref="dropdownRef"
      :name="`wfb-template-select-${model.id}`"
      :value="model.workflow_template_id"
      :placeholder="`${$t('COMMON.SELECT')} ${$t('COMMON.TEMPLATE')}`"
      :search-term="$t('WORKFLOW.TEMPLATES', 1)"
      :get-paginated-options="getWorkflowTemplatesForInternalFundingClient"
      :warning-as-error="warningAsError"
      accessor="name"
      full-width
      as-badge
      :teleport-options="{
        enabled: true,
        scrollable: 'dealsWrapper',
      }"
      @change="handleWorkflowTemplateChange"
    />
  </div>
</template>

<script lang="ts">
import { onMounted, ref, watch } from "vue";

import { EMPTY_RESOURCE, ERROR_DURATION } from "@/helpers/constants";
import { PLACEMENT_NO_WFB_TEMPLATE } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import workflowApiService from "@/services/modules/workflows";

import type { IFunder } from "@/models/funders";
import type {
  IWorkflow,
  WorkflowTemplatesPayloadParams,
} from "@/models/workflows";
</script>

<script setup lang="ts">
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";

const { model } = defineProps<{
  model: IFunder;
}>();

const emit = defineEmits<{
  change: [funderId: string, workflowTemplateId: string];
}>();

const emitter = useEmitter();
const warningAsError = ref(false);
const dropdownWrapper = ref<HTMLDivElement | null>(null);
const dropdownRef = ref<InstanceType<typeof LfDropdownWithApiSearch> | null>(
  null
);

emitter.on(PLACEMENT_NO_WFB_TEMPLATE, ({ id, focus }) => {
  const matches = model.id === id;
  if (!matches) {
    return;
  }
  if (focus) {
    dropdownWrapper.value?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }
  warningAsError.value = true;
  setTimeout(() => {
    warningAsError.value = false;
  }, ERROR_DURATION);
});

const getWorkflowTemplatesForInternalFundingClient = async (params: {
  page: number;
  search: string | null;
}) => {
  const clientId = model.client_id || "";
  if (!clientId) {
    return EMPTY_RESOURCE;
  }
  const payload: WorkflowTemplatesPayloadParams = {
    active: "1",
    approved: "1",
    category: "internal_funding",
    clients: [clientId],
    ...params,
  };
  return await workflowApiService.getWorkflowTemplates(payload);
};

const handleWorkflowTemplateChange = (workflowTemplate: IWorkflow) => {
  emit("change", model.id, workflowTemplate.id);
};

const preloadSelectedTemplate = async (templateId: string) => {
  await dropdownRef.value?.loadMore({
    workflow_template_ids: [templateId],
  });
  dropdownRef.value?.resetPaginatedOptionsMeta();
};

onMounted(() => {
  if (model.workflow_template_id) {
    preloadSelectedTemplate(model.workflow_template_id);
  }
});

watch(
  () => model.workflow_template_id,
  (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      preloadSelectedTemplate(newValue);
    }
  }
);
</script>
